import { useUser } from '@clerk/react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  BookOpen,
  Layers,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  Sparkles,
  Tags,
  Users,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { useSidebarCollapsed } from '@/hooks/useSidebarCollapsed';
import { posthog } from '@/lib/posthog';
import { useAuth } from '../auth/AuthContext';
import { useCurrentRole } from '../auth/useCurrentRole';

export function RootLayout({ children }: { children: ReactNode }) {
  const { signOut } = useAuth();
  const role = useCurrentRole();

  return (
    <>
      <AnalyticsObservers />
      <RootLayoutInner role={role} signOut={signOut}>
        {children}
      </RootLayoutInner>
    </>
  );
}

/**
 * Side-effect-only component that wires PostHog page-view tracking +
 * Clerk → PostHog user identification. Renders nothing. Lives inside
 * RootLayout so it benefits from the same provider tree (Router, Clerk,
 * QueryClient).
 */
function AnalyticsObservers() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const { user, isLoaded } = useUser();

  useEffect(() => {
    posthog.capture('$pageview', { path });
  }, [path]);

  useEffect(() => {
    if (!isLoaded) return;
    if (user) {
      posthog.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
      });
    } else {
      // Sign-out path — clears the identified profile so subsequent
      // captures don't bleed into the previous user's session.
      posthog.reset();
    }
  }, [isLoaded, user]);

  return null;
}

function RootLayoutInner({
  role,
  signOut,
  children,
}: {
  role: ReturnType<typeof useCurrentRole>;
  signOut: () => void;
  children: ReactNode;
}) {
  if (!role.isAuthReady) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <LoadingShell>{children}</LoadingShell>
      </div>
    );
  }
  if (!role.isSignedIn) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SignedOutLayout>{children}</SignedOutLayout>
      </div>
    );
  }

  // Two chromes — role derived synchronously from Clerk JWT claims so
  // there's no loading state to worry about between Clerk-loaded and
  // role-decided.
  return (
    <div className="min-h-screen bg-background text-foreground">
      {role.isPractitioner ? (
        <InternalLayout signOut={signOut}>{children}</InternalLayout>
      ) : (
        <PortalLayout signOut={signOut}>{children}</PortalLayout>
      )}
    </div>
  );
}

function LoadingShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-5xl items-center px-6 py-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-brand-navy hover:no-underline"
          >
            <Logo size={28} />
            <span className="text-base font-semibold tracking-tight">
              InputRecovery
            </span>
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">{children}</main>
    </div>
  );
}

function InternalLayout({
  signOut,
  children,
}: {
  signOut: () => void;
  children: ReactNode;
}) {
  // No max-width cap on the internal app. The runs review page renders
  // wide tables (the NetSuite journal entry has up to 22 columns) and
  // the practitioner wants the whole screen for it. Forms / narrow
  // content on other internal pages handle their own max-width
  // explicitly (sign-in, account-create, etc.), so removing the cap
  // here doesn't stretch any single-column flows.
  return (
    <div className="flex min-h-screen">
      <Sidebar signOut={signOut} />
      <main className="min-w-0 flex-1 px-6 py-8 sm:px-10 sm:py-10">
        {children}
      </main>
    </div>
  );
}

/**
 * Customer-facing chrome — completely different from the internal app.
 * No sidebar (the portal has only one section today; add nav when it
 * grows). Header carries the logo, the user's email, and a Sign out
 * button. Content area is centered + narrower for a one-screen feel.
 */
function PortalLayout({
  signOut,
  children,
}: {
  signOut: () => void;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b border-border bg-secondary/20">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-6 py-3">
          <Link
            to="/portal"
            className="flex items-center gap-2 text-brand-navy hover:no-underline"
          >
            <Logo size={24} />
            <span className="text-sm font-semibold tracking-tight">
              InputRecovery portal
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <CurrentUserEmail />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={signOut}
              className="gap-2 text-brand-slate hover:text-brand-royal"
            >
              <LogOut size={16} strokeWidth={1.75} />
              <span>Sign out</span>
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 px-6 py-10">
        <div className="mx-auto max-w-3xl">{children}</div>
      </main>
    </div>
  );
}

function Sidebar({ signOut }: { signOut: () => void }) {
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  return (
    <aside
      className={`flex shrink-0 flex-col border-r border-border bg-secondary/30 transition-[width] duration-200 ${
        collapsed ? 'w-14' : 'w-56'
      }`}
    >
      {/* flex-col-reverse keeps the toggle at the top when collapsed while
          leaving the home link below it (and the brand on the left, toggle
          on the right when expanded) — one DOM order, both layouts. */}
      <div
        className={`flex border-b border-border py-4 ${
          collapsed
            ? 'flex-col-reverse items-center gap-3 px-2'
            : 'items-center justify-between px-5'
        }`}
      >
        <Link
          to="/"
          title={collapsed ? 'InputRecovery' : undefined}
          className="flex items-center gap-2 text-brand-navy hover:no-underline"
        >
          <Logo size={24} />
          {!collapsed && (
            <span className="text-sm font-semibold tracking-tight">InputRecovery</span>
          )}
        </Link>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="text-brand-slate hover:text-brand-royal"
        >
          {collapsed ? (
            <PanelLeftOpen size={16} strokeWidth={1.75} />
          ) : (
            <PanelLeftClose size={16} strokeWidth={1.75} />
          )}
        </Button>
      </div>
      <nav className="flex flex-1 flex-col gap-4 p-3">
        <div className="flex flex-col gap-1">
          <SidebarLink
            to="/customers"
            collapsed={collapsed}
            icon={<Users size={16} strokeWidth={1.75} />}
          >
            Customers
          </SidebarLink>
        </div>
        <div className="flex flex-col gap-1">
          {!collapsed && <SidebarGroupHeading>Tools</SidebarGroupHeading>}
          <SidebarLink
            to="/labeling"
            collapsed={collapsed}
            icon={<Tags size={16} strokeWidth={1.75} />}
          >
            Label invoices
          </SidebarLink>
        </div>
        <div className="flex flex-col gap-1">
          {!collapsed && <SidebarGroupHeading>Reference</SidebarGroupHeading>}
          <SidebarLink
            to="/rules"
            collapsed={collapsed}
            icon={<BookOpen size={16} strokeWidth={1.75} />}
          >
            Rules
          </SidebarLink>
          <SidebarLink
            to="/rule-sets"
            collapsed={collapsed}
            icon={<Layers size={16} strokeWidth={1.75} />}
          >
            Rule sets
          </SidebarLink>
          <SidebarLink
            to="/ask-kb"
            collapsed={collapsed}
            icon={<Sparkles size={16} strokeWidth={1.75} />}
          >
            Ask the KB
          </SidebarLink>
        </div>
      </nav>
      <div className="flex flex-col gap-1 border-t border-border p-3">
        <SidebarLink
          to="/settings"
          collapsed={collapsed}
          icon={<Settings size={16} strokeWidth={1.75} />}
        >
          Settings
        </SidebarLink>
        {!collapsed && <CurrentUserEmail />}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={signOut}
          title={collapsed ? 'Sign out' : undefined}
          className={`gap-2 text-brand-slate hover:text-brand-royal ${
            collapsed ? 'justify-center px-0' : 'justify-start px-3'
          }`}
        >
          <LogOut size={16} strokeWidth={1.75} />
          {!collapsed && <span>Sign out</span>}
        </Button>
      </div>
    </aside>
  );
}

function CurrentUserEmail() {
  // Read directly from Clerk so we don't pay an /api/me round-trip in
  // every render. Render nothing if Clerk hasn't hydrated yet — the
  // sidebar is fine with one fewer line for a few hundred ms.
  const { user } = useUser();
  const email = user?.primaryEmailAddress?.emailAddress;
  if (!email) return null;
  return (
    <p
      className="truncate px-3 pt-1 pb-0.5 text-xs text-brand-slate/80"
      title={email}
    >
      {email}
    </p>
  );
}

function SidebarLink({
  to,
  icon,
  collapsed,
  children,
}: {
  to: '/customers' | '/settings' | '/rules' | '/rule-sets' | '/ask-kb' | '/labeling';
  icon: ReactNode;
  collapsed: boolean;
  children: string;
}) {
  const layout = collapsed
    ? 'flex items-center justify-center rounded-md px-0 py-2 text-sm'
    : 'flex items-center gap-2 rounded-md px-3 py-2 text-sm';
  return (
    <Link
      to={to}
      title={collapsed ? children : undefined}
      className={`${layout} text-brand-slate hover:bg-secondary hover:text-brand-royal`}
      activeProps={{
        className: `${layout} bg-secondary text-brand-royal font-medium`,
      }}
    >
      {icon}
      {!collapsed && <span>{children}</span>}
    </Link>
  );
}

function SidebarGroupHeading({ children }: { children: ReactNode }) {
  return (
    <p className="px-3 text-xs font-medium uppercase tracking-wide text-brand-slate/70">
      {children}
    </p>
  );
}

function SignedOutLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen">
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-brand-navy hover:no-underline"
          >
            <Logo size={28} />
            <span className="text-base font-semibold tracking-tight">InputRecovery</span>
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-10">{children}</main>
    </div>
  );
}
